import React, { useState, useEffect, useCallback, memo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";

const UpdateAgent = memo(() => {
  const { id } = useParams();
  const { currentUser } = useSelector((state) => state.user);
  const [agent, setAgent] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Memoized fetch function
  const fetchAgent = useCallback(async () => {
    try {
      const response = await axios.get(`/api/user/branch/${id}`);
      setAgent(response.data);
    } catch (err) {
      console.error("Fetch agent error:", err);
      setError("Error fetching agent details");
    }
  }, [id]);

  useEffect(() => {
    fetchAgent();
  }, [fetchAgent]);

  // Form handlers
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setAgent(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Only send password if it was changed
      const dataToSend = { ...agent };
      if (!dataToSend.password) {
        delete dataToSend.password;
      }

      const response = await axios.post(
        `/api/user/updatebranch/${id}`,
        dataToSend
      );

      if (response.data) {
        setError("Agent updated successfully!");
      } else {
        setError("Failed to update agent");
      }
    } catch (err) {
      console.error("Update error:", err);
      setError(err.response?.data?.message || "Error updating agent");
    } finally {
      setLoading(false);
    }
  }, [agent, id]);

  return (
    <div className="tw-bg-slate-100 tw-flex tw-justify-center tw-items-center tw-min-h-screen">
      <div className="tw-w-full tw-max-w-md tw-p-8 tw-rounded-md tw-shadow-md tw-border-l-8 tw-border-l-blue-600 tw-bg-white">
        <h2 className="tw-text-lg tw-font-semibold tw-mb-4">Edit Agent</h2>
        {error && <p className="tw-text-red-500 tw-mb-4">{error}</p>}
        
        <form onSubmit={handleSubmit}>
          {[
            { id: "name", label: "Name", type: "text", required: true },
            { id: "username", label: "Username", type: "text", required: true },
            { id: "phone", label: "Phone", type: "tel", required: true },
            { id: "cut", label: "Cut", type: "number", required: true },
            { id: "password", label: "Password", type: "password", required: false }
          ].map((field) => (
            <div key={field.id} className="tw-mb-4">
              <label className="tw-block tw-text-gray-700 tw-mb-2" htmlFor={field.id}>
                {field.label}
              </label>
              <input
                className="tw-w-full tw-px-3 tw-py-2 tw-rounded-md tw-border tw-border-gray-300 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                type={field.type}
                name={field.id}
                id={field.id}
                value={agent[field.id] || ""}
                onChange={handleChange}
                required={field.required}
              />
            </div>
          ))}

          <div className="tw-mb-4">
            <label className="tw-block tw-text-gray-700 tw-mb-2" htmlFor="role">
              Role
            </label>
            <select
              name="role"
              value={agent.role || "employee"}
              onChange={handleChange}
              className="tw-w-full tw-rounded-lg tw-border tw-border-slate-300 tw-p-2.5 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
            >
              <option value="employee">Employee</option>
              <option value="admin">Admin</option>
              <option value="agent">Agent</option>
              {currentUser.role === "superadmin" && (
                <option value="superadmin">Super Admin</option>
              )}
            </select>
          </div>

          <div className="tw-flex tw-justify-end tw-mt-4">
            <button
              type="submit"
              disabled={loading}
              className="tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-md hover:tw-bg-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 disabled:tw-opacity-50"
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

export default UpdateAgent;